import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "emailPanel",
    "nostrPanel",
    "emailField",
    "passwordField",
    "passwordConfirmationField",
    "eventField",
    "status",
    "submitLabel",
    "switchLabel",
    "toggle",
    "launchButton"
  ];

  static values = {
    mode: { type: String, default: "email" },
    intent: String,
    challengePath: String,
    preloadedChallenge: String,
    callbackPath: String,
    nostrPath: String,
    emailLabel: String,
    nostrLabel: String
  };

  connect() {
    this.form = this.element.tagName === "FORM" ? this.element : this.element.querySelector("form");
    this.submitting = false;
    this.challengeRequest = null;
    this.debugLogPath = "/PB/users/nostr/lab_log";
    this._resumeOnPageShow = () => this.resumeSignerCallback();
    this._resumeOnFocus = () => this.resumeSignerCallback();
    this._resumeOnVisibilityChange = () => {
      if (document.visibilityState === "visible") this.resumeSignerCallback();
    };

    window.addEventListener("pageshow", this._resumeOnPageShow);
    window.addEventListener("focus", this._resumeOnFocus);
    document.addEventListener("visibilitychange", this._resumeOnVisibilityChange);

    this.loadPreloadedChallenge();
    this.applyMode(this.modeValue || "email");
    this.restorePendingTurnstileToken();
    this.resumeSignerCallback();
    this.warmChallenge();
    this.reportSignerAvailability();
    this.debugLog("connect", `intent=${this.intentValue} host=${window.location.host}`);
  }

  disconnect() {
    window.removeEventListener("pageshow", this._resumeOnPageShow);
    window.removeEventListener("focus", this._resumeOnFocus);
    document.removeEventListener("visibilitychange", this._resumeOnVisibilityChange);
  }

  toggleMode(event) {
    event.preventDefault();
    const explicitMode = event.currentTarget.dataset.nostrMode;
    const nextMode = explicitMode || (this.currentMode === "email" ? "nostr" : "email");
    this.applyMode(nextMode);
  }

  captureTurnstile(event) {
    const token = event?.detail?.token?.toString().trim();
    if (!token) return;

    this.turnstileFields().forEach((field) => {
      field.value = token;
    });
    this.persistTurnstileToken();
    this.debugLog("turnstile_token_ok", `${this.intentValue}:${token.slice(0, 18)}`);
  }

  async submit(event) {
    if (this.currentMode !== "nostr" || this.submitting) return;

    event.preventDefault();
    if (this.hasPendingSignedEvent()) {
      this.submitSignedEvent();
      return;
    }
    await this.signAndSubmit();
  }

  async standaloneSubmit(event) {
    event.preventDefault();
    if (this.submitting) return;
    if (this.hasPendingSignedEvent()) {
      this.submitSignedEvent();
      return;
    }
    await this.signAndSubmit();
  }

  applyMode(mode) {
    this.currentMode = mode;
    const emailMode = mode === "email";

    if (this.hasEmailPanelTarget) this.emailPanelTarget.hidden = !emailMode;
    if (this.hasNostrPanelTarget) this.nostrPanelTarget.hidden = emailMode;

    this.emailFieldTargets.forEach((field) => {
      field.required = emailMode && field.dataset.nostrRequired !== "false";
      field.disabled = !emailMode && field.dataset.nostrDisableWhenHidden === "true";
    });

    this.passwordFieldTargets.forEach((field) => {
      field.required = emailMode;
      field.disabled = !emailMode && field.dataset.nostrDisableWhenHidden === "true";
    });

    this.passwordConfirmationFieldTargets.forEach((field) => {
      field.required = emailMode;
      field.disabled = !emailMode && field.dataset.nostrDisableWhenHidden === "true";
    });

    this.toggleTargets.forEach((toggle) => {
      toggle.classList.toggle("is-active", toggle.dataset.nostrMode === mode);
    });

    const label = emailMode ? this.emailLabelValue : this.nostrLabelValue;
    this.submitLabelTargets.forEach((target) => {
      if (target.tagName === "INPUT") {
        target.value = label;
      } else {
        target.textContent = label;
      }
    });

    this.switchLabelTargets.forEach((target) => {
      target.textContent = emailMode ? "nostr" : "✉️";
    });

    this.setStatus("");

    if (!emailMode) {
      this.warmChallenge();
    }
  }

  async signAndSubmit() {
    if (!this.form) return;
    if (typeof this.form.reportValidity === "function" && !this.form.reportValidity()) {
      return;
    }

    this.submitting = true;
    if (this.hasLaunchButtonTarget) this.launchButtonTarget.disabled = true;
    this.setStatus("Waiting for your Nostr signer...");
    this.debugLog("sign_start", `intent=${this.intentValue} host=${window.location.host}`);

    try {
      if (this.hasNip07Signer()) {
        this.debugLog("nip07_detected", "window.nostr available");
        const cachedChallenge = this.readCachedChallenge();
        if (!cachedChallenge) {
          await this.warmChallenge({ force: true });
          this.submitting = false;
          if (this.hasLaunchButtonTarget) this.launchButtonTarget.disabled = false;
          this.setStatus("Nostr signer detected. Challenge is ready now. Press the same button again.");
          this.debugLog("nip07_challenge_ready", "challenge warmed, awaiting second tap");
          return;
        }

        const unsignedEvent = this.buildUnsignedEvent(cachedChallenge);
        this.clearSignerPhase();
        this.setStatus("Requesting signature from your Nostr signer...");
        this.debugLog("nip07_request_sign", JSON.stringify(unsignedEvent));
        const signedEvent = await this.signWithNip07(unsignedEvent);
        const normalizedPubkey = this.normalizeReturnedPubkey(signedEvent?.pubkey);
        if (normalizedPubkey) {
          this.storeSignerPubkey(normalizedPubkey);
        }

        this.storeSignedEvent(JSON.stringify(signedEvent));
        this.setStatus("Signed. Entering the Pinball lane...");
        this.debugLog("nip07_signed", JSON.stringify(signedEvent));
        this.submitSignedEvent();
        return;
      }

      const challenge = await this.warmChallenge({ force: this.requiresFreshChallenge() });
      const unsignedEvent = this.buildUnsignedEvent(challenge);
      this.debugLog("external_challenge_ready", JSON.stringify(unsignedEvent));

      if (this.canUseExternalSigner()) {
        this.storePendingUnsignedEvent(unsignedEvent);
        const signerPubkey = this.normalizeReturnedPubkey(this.readSignerPubkey());

        if (!signerPubkey) {
          this.debugLog("external_request_pubkey", "launching signer connect");
          this.launchExternalPubkeyRequest();
          return;
        }

        this.debugLog("external_request_sign", `launching external signer for ${signerPubkey}`);
        this.launchExternalSignEvent(unsignedEvent, signerPubkey);
        return;
      }

      this.debugLog("no_signer", "no nip07 and no supported external signer");
      throw new Error("No Nostr signer detected. Amber on Android needs an external signer handoff or a NIP-07 browser signer.");
    } catch (error) {
      this.submitting = false;
      if (this.hasLaunchButtonTarget) this.launchButtonTarget.disabled = false;
      this.setStatus(error.message || "Nostr signing failed.");
      this.debugLog("sign_error", error.message || "Nostr signing failed.");
    }
  }

  async fetchChallenge() {
    this.debugLog("challenge_fetch_start", this.challengePathValue);
    const challengeResponse = await fetch(`${this.challengePathValue}?intent=${encodeURIComponent(this.intentValue)}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    });
    const challenge = await challengeResponse.json();

    if (!challengeResponse.ok) {
      this.debugLog("challenge_fetch_error", challenge.error || `HTTP ${challengeResponse.status}`);
      throw new Error(challenge.error || "Could not start the Nostr lane.");
    }

    this.debugLog("challenge_fetch_ok", JSON.stringify(challenge));
    return challenge;
  }

  async warmChallenge({ force = false } = {}) {
    if (!this.intentValue || !this.challengePathValue) return null;

    const cachedChallenge = force ? null : this.readCachedChallenge();
    if (cachedChallenge) return cachedChallenge;

    if (this.challengeRequest && !force) return this.challengeRequest;

    this.challengeRequest = this.fetchChallenge()
      .then((challenge) => {
        this.storeCachedChallenge(challenge);
        return challenge;
      })
      .finally(() => {
        this.challengeRequest = null;
      });

    return this.challengeRequest;
  }

  buildUnsignedEvent(challenge) {
    return {
      kind: challenge.kind,
      created_at: Math.floor(Date.now() / 1000),
      tags: challenge.tags || [],
      content: challenge.message
    };
  }

  hasNip07Signer() {
    return !!(window.nostr && typeof window.nostr.signEvent === "function");
  }

  async signWithNip07(unsignedEvent) {
    const signer = window.nostr;
    if (!signer || typeof signer.signEvent !== "function") {
      throw new Error("No Nostr signer detected.");
    }

    const signedEvent = await this.withSignerTimeout(
      signer.signEvent(unsignedEvent),
      `Your Nostr signer did not respond. Approve ${this.signerHostLabel()} inside the signer, then press the button again.`
    );

    let normalizedPubkey = this.normalizeReturnedPubkey(signedEvent?.pubkey);
    if (!normalizedPubkey) {
      normalizedPubkey = await this.fetchNip07Pubkey(signer);
    }

    if (normalizedPubkey && !signedEvent?.pubkey) {
      signedEvent.pubkey = normalizedPubkey;
    }

    if (!normalizedPubkey || !signedEvent?.sig || !signedEvent?.id) {
      throw new Error("Signer returned an incomplete event.");
    }

    return signedEvent;
  }

  async fetchNip07Pubkey(signer) {
    const cachedPubkey = this.normalizeReturnedPubkey(this.readSignerPubkey());
    if (cachedPubkey) return cachedPubkey;

    if (!signer || typeof signer.getPublicKey !== "function") return null;

    this.setStatus("Requesting public key permission from your Nostr signer...");
    this.debugLog("nip07_request_pubkey", "window.nostr.getPublicKey");
    const signerPubkey = await this.withSignerTimeout(
      signer.getPublicKey(),
      `Your Nostr signer did not share a public key. Approve ${this.signerHostLabel()} inside the signer, then press the button again.`
    );

    const normalizedPubkey = this.normalizeReturnedPubkey(signerPubkey);
    if (!normalizedPubkey) {
      throw new Error("Signer did not return a valid public key.");
    }

    this.storeSignerPubkey(normalizedPubkey);
    this.debugLog("nip07_pubkey_ok", normalizedPubkey);
    return normalizedPubkey;
  }

  async withSignerTimeout(promise, message, timeoutMs = 12000) {
    let timeoutId;
    const timeoutPromise = new Promise((_, reject) => {
      timeoutId = window.setTimeout(() => reject(new Error(message)), timeoutMs);
    });

    try {
      return await Promise.race([promise, timeoutPromise]);
    } finally {
      window.clearTimeout(timeoutId);
    }
  }

  reportSignerAvailability() {
    if (this.intentValue !== "link") return;
    if (this.hasNip07Signer()) {
      this.setStatus("Nostr signer detected on this page.");
      this.debugLog("report_signer", "nip07 detected");
    } else {
      this.debugLog("report_signer", "nip07 missing");
    }
  }

  canUseExternalSigner() {
    const ua = navigator.userAgent || "";
    return /Android/i.test(ua);
  }

  launchExternalPubkeyRequest() {
    this.persistTurnstileToken();
    this.storeStoredIntent();
    this.storeSignerPhase("get_public_key");
    this.setStatus("Opening Amber...");
    this.debugLog("amber_open_pubkey", this.signerCallbackUrl());

    const signerUrl = `nostrsigner:?compressionType=none&returnType=signature&type=get_public_key&callbackUrl=${encodeURIComponent(this.signerCallbackUrl())}`;
    this.openSignerUrl(signerUrl);
  }

  launchExternalSignEvent(unsignedEvent, currentUserPubkey = this.readSignerPubkey()) {
    this.persistTurnstileToken();
    this.storeStoredIntent();
    this.storeSignerPhase("sign_event");
    this.storePendingUnsignedEvent(unsignedEvent);
    this.setStatus("Opening Amber...");
    this.debugLog("amber_open_sign", JSON.stringify(unsignedEvent));

    const encodedEvent = encodeURIComponent(JSON.stringify(unsignedEvent));
    const signerParams = new URLSearchParams({
      compressionType: "none",
      returnType: "signature",
      type: "sign_event",
      callbackUrl: this.signerCallbackUrl()
    });

    if (currentUserPubkey) signerParams.set("current_user", currentUserPubkey);
    signerParams.set("id", `${this.intentValue}-${unsignedEvent.created_at}`);

    const signerUrl = `nostrsigner:${encodedEvent}?${signerParams.toString()}`;
    this.openSignerUrl(signerUrl);
  }

  openSignerUrl(url) {
    const launchLink = document.createElement("a");
    launchLink.href = url;
    launchLink.rel = "noopener";
    launchLink.style.display = "none";
    document.body.appendChild(launchLink);
    launchLink.click();
    launchLink.remove();

    window.setTimeout(() => {
      if (document.visibilityState === "visible") {
        window.location.href = url;
      }
    }, 150);
  }

  signerCallbackUrl() {
    const url = new URL(window.location.href);
    url.hash = "";
    url.searchParams.delete("intent");
    url.searchParams.delete("event");
    url.searchParams.delete("nostr_event");
    url.searchParams.delete("result");
    url.searchParams.delete("signature");
    url.searchParams.delete("sig");
    url.searchParams.delete("pubkey");
    url.searchParams.delete("current_user");
    url.searchParams.delete("nostr_intent");
    url.searchParams.delete("nostr_error");
    url.searchParams.set("nostr_intent", this.intentValue);
    url.searchParams.set("event", "");
    return url.toString();
  }

  async resumeSignerCallback() {
    const url = new URL(window.location.href);
    const params = this.callbackParams(url);
    const inlineEvent = this.inlineEventFromIntent(params.get("nostr_intent"));
    const returnedIntent = this.normalizedIntentFromParams(params);
    const returnedEvent = params.get("event") || params.get("nostr_event") || params.get("result") || inlineEvent;
    const returnedSignature = params.get("signature") || params.get("sig");
    const returnedPubkey = params.get("pubkey") || params.get("current_user");
    const returnedError = params.get("nostr_error");
    const signerPhase = this.readSignerPhase();

    this.debugLog("resume_callback", `intent=${returnedIntent || "none"} phase=${signerPhase || "none"} keys=${Array.from(params.keys()).join(",")}`);

    if (returnedIntent !== this.intentValue) return;

    this.applyMode("nostr");

    if (returnedError) {
      this.cleanSignerCallbackParams(url);
      this.clearStoredIntent();
      this.clearSignerPhase();
      this.setStatus("Amber rejected the request or did not return a signed event.");
      this.debugLog("resume_error", returnedError);
      return;
    }

    if (signerPhase === "get_public_key") {
      const signerPubkey = this.normalizeReturnedPubkey(returnedEvent || returnedSignature || returnedPubkey);
      if (!this.validPubkey(signerPubkey)) return;

      this.storeSignerPubkey(signerPubkey);
      this.cleanSignerCallbackParams(url);
      this.clearSignerPhase();

      const pendingUnsignedEvent = this.readPendingUnsignedEvent();
      if (!pendingUnsignedEvent) {
        this.submitting = false;
        if (this.hasLaunchButtonTarget) this.launchButtonTarget.disabled = false;
        this.setStatus("Amber shared your public key. Press the same button again to sign in.");
        this.debugLog("amber_pubkey_ok", signerPubkey);
        return;
      }

      this.setStatus("Amber shared your public key. Requesting signature...");
      this.debugLog("amber_pubkey_sign_next", signerPubkey);
      this.launchExternalSignEvent(pendingUnsignedEvent, signerPubkey);
      return;
    }

    const normalizedEvent = await this.normalizeReturnedEvent(returnedEvent);
    const signature = this.normalizeReturnedSignature(normalizedEvent) || this.normalizeReturnedSignature(returnedSignature);
    const signedEvent = this.normalizedEventJson(normalizedEvent) || await this.rebuildSignedEvent(signature, returnedPubkey);
    if (!signedEvent) return;
    this.debugLog("resume_signed_event", signedEvent);

    if (returnedPubkey) {
      this.storeSignerPubkey(returnedPubkey);
    }

    this.storeSignedEvent(signedEvent);
    this.clearPendingUnsignedEvent();
    this.clearStoredIntent();
    this.clearCachedChallenge();
    this.clearSignerPhase();
    this.restorePendingTurnstileToken();
    this.cleanSignerCallbackParams(url);

    if (this.turnstileReady()) {
      this.setStatus(this.intentValue === "link" ? "Amber signed the challenge. Linking your Nostr account..." : "Amber signed the challenge. Entering the Pinball lane...");
      this.debugLog("resume_submit_signed", this.intentValue);
      this.submitSignedEvent();
    } else if (this.requiresTurnstile()) {
      this.submitting = false;
      if (this.hasLaunchButtonTarget) this.launchButtonTarget.disabled = false;
      this.setStatus("Amber signed the challenge. Complete the Cloudflare check, then press the same button again.");
      this.debugLog("resume_wait_turnstile", this.intentValue);
    } else {
      this.debugLog("resume_submit_without_turnstile", this.intentValue);
      this.submitSignedEvent();
    }
  }

  async rebuildSignedEvent(signature, callbackPubkey) {
    const normalizedSignature = signature?.toString().trim();
    if (!normalizedSignature || !/^[0-9a-f]{128}$/i.test(normalizedSignature)) return null;

    const unsignedEvent = this.readPendingUnsignedEvent();
    if (!unsignedEvent) return null;

    const pubkey = this.normalizeReturnedPubkey(callbackPubkey) || this.readSignerPubkey();
    if (!pubkey || !/^[0-9a-f]{64}$/i.test(pubkey)) return null;

    const eventId = await this.computeEventId({ ...unsignedEvent, pubkey });
    return JSON.stringify({
      ...unsignedEvent,
      pubkey,
      id: eventId,
      sig: normalizedSignature
    });
  }

  async computeEventId(event) {
    const payload = JSON.stringify([0, event.pubkey, event.created_at, event.kind, event.tags || [], event.content]);
    const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(payload));
    return Array.from(new Uint8Array(digest)).map((value) => value.toString(16).padStart(2, "0")).join("");
  }

  cleanSignerCallbackParams(url = new URL(window.location.href)) {
    url.searchParams.delete("event");
    url.searchParams.delete("nostr_event");
    url.searchParams.delete("result");
    url.searchParams.delete("signature");
    url.searchParams.delete("sig");
    url.searchParams.delete("pubkey");
    url.searchParams.delete("current_user");
    url.searchParams.delete("nostr_intent");
    url.searchParams.delete("nostr_error");
    if (url.hash.startsWith("#")) {
      const hashParams = new URLSearchParams(url.hash.slice(1));
      hashParams.delete("event");
      hashParams.delete("nostr_event");
      hashParams.delete("result");
      hashParams.delete("signature");
      hashParams.delete("sig");
      hashParams.delete("pubkey");
      hashParams.delete("current_user");
      hashParams.delete("nostr_intent");
      hashParams.delete("nostr_error");
      const nextHash = hashParams.toString();
      url.hash = nextHash ? `#${nextHash}` : "";
    }
    window.history.replaceState({}, "", url.toString());
  }

  callbackParams(url = new URL(window.location.href)) {
    const params = new URLSearchParams(url.search);
    if (url.hash.startsWith("#")) {
      const hashParams = new URLSearchParams(url.hash.slice(1));
      hashParams.forEach((value, key) => {
        if (!params.has(key)) params.set(key, value);
      });
    }
    return params;
  }

  normalizedIntentFromParams(params) {
    const rawIntent = params.get("nostr_intent") || params.get("intent");
    if (!rawIntent) return this.readStoredIntent();

    for (const intent of ["sign_in", "sign_up", "link"]) {
      if (rawIntent === intent || rawIntent.startsWith(`${intent}Signer1`) || rawIntent.startsWith(`${intent}{`)) {
        return intent;
      }
    }

    return rawIntent;
  }

  inlineEventFromIntent(rawIntent) {
    if (!rawIntent) return null;

    for (const intent of ["sign_in", "sign_up", "link"]) {
      if (rawIntent.startsWith(`${intent}Signer1`) || rawIntent.startsWith(`${intent}{`)) {
        return rawIntent.slice(intent.length);
      }
    }

    return null;
  }

  async normalizeReturnedEvent(rawValue) {
    if (!rawValue) return rawValue;
    if (rawValue.trim().startsWith("{")) return rawValue;
    if (!rawValue.startsWith("Signer1")) return rawValue;

    const payload = rawValue.slice("Signer1".length);
    const binary = Uint8Array.from(atob(payload), (char) => char.charCodeAt(0));

    if (typeof DecompressionStream === "undefined") {
      return new TextDecoder().decode(binary);
    }

    const stream = new Response(
      new Blob([binary]).stream().pipeThrough(new DecompressionStream("gzip"))
    );

    return await stream.text();
  }

  hasPendingSignedEvent() {
    return this.eventFieldTargets.some((field) => field.value && field.value.trim().length > 0);
  }

  storeSignedEvent(eventJson) {
    this.eventFieldTargets.forEach((field) => {
      field.value = eventJson;
    });
  }

  submitSignedEvent() {
    if (!this.form || !this.hasPendingSignedEvent()) return;
    this.submitting = true;
    this.restorePendingTurnstileToken();
    this.clearPendingTurnstileToken();
    this.form.action = this.nostrPathValue;
    this.form.dataset.turbo = "false";
    this.form.submit();
  }

  turnstileFields() {
    return Array.from(this.form?.querySelectorAll('input[name="cf-turnstile-response"]') || []);
  }

  requiresTurnstile() {
    return !!this.form?.querySelector(".cf-turnstile");
  }

  turnstileReady() {
    return !this.requiresTurnstile() || this.turnstileFields().some((field) => field.value && field.value.trim().length > 0);
  }

  pendingTurnstileStorageKey() {
    return `pinball:nostr-turnstile:${this.intentValue}`;
  }

  challengeStorageKey() {
    return `pinball:nostr-challenge:${this.intentValue}`;
  }

  pendingUnsignedEventStorageKey() {
    return `pinball:nostr-unsigned:${this.intentValue}`;
  }

  storedIntentKey() {
    return "pinball:nostr-intent";
  }

  signerPubkeyStorageKey() {
    return "pinball:nostr-signer-pubkey";
  }

  signerPhaseStorageKey() {
    return `pinball:nostr-phase:${this.intentValue}`;
  }

  readCachedChallenge() {
    try {
      const raw = sessionStorage.getItem(this.challengeStorageKey());
      if (!raw) return null;

      const payload = JSON.parse(raw);
      if (!payload?.challenge || !payload.fetchedAt) return null;
      if ((Date.now() - payload.fetchedAt) > 4 * 60 * 1000) {
        sessionStorage.removeItem(this.challengeStorageKey());
        return null;
      }

      return payload.challenge;
    } catch (_) {
      return null;
    }
  }

  loadPreloadedChallenge() {
    if (!this.hasPreloadedChallengeValue || !this.preloadedChallengeValue) return;

    try {
      const parsed = JSON.parse(this.preloadedChallengeValue);
      if (parsed?.message) {
        this.storeCachedChallenge(parsed);
      }
    } catch (_) {}
  }

  storeCachedChallenge(challenge) {
    try {
      sessionStorage.setItem(this.challengeStorageKey(), JSON.stringify({
        challenge,
        fetchedAt: Date.now()
      }));
    } catch (_) {}
  }

  clearCachedChallenge() {
    try {
      sessionStorage.removeItem(this.challengeStorageKey());
    } catch (_) {}
  }

  requiresFreshChallenge() {
    return !this.readCachedChallenge();
  }

  storePendingUnsignedEvent(unsignedEvent) {
    try {
      sessionStorage.setItem(this.pendingUnsignedEventStorageKey(), JSON.stringify(unsignedEvent));
    } catch (_) {}
  }

  readPendingUnsignedEvent() {
    try {
      const raw = sessionStorage.getItem(this.pendingUnsignedEventStorageKey());
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (_) {
      return null;
    }
  }

  clearPendingUnsignedEvent() {
    try {
      sessionStorage.removeItem(this.pendingUnsignedEventStorageKey());
    } catch (_) {}
  }

  storeStoredIntent() {
    try {
      sessionStorage.setItem(this.storedIntentKey(), this.intentValue);
    } catch (_) {}
  }

  readStoredIntent() {
    try {
      return sessionStorage.getItem(this.storedIntentKey());
    } catch (_) {
      return null;
    }
  }

  clearStoredIntent() {
    try {
      sessionStorage.removeItem(this.storedIntentKey());
    } catch (_) {}
  }

  storeSignerPubkey(pubkey) {
    try {
      sessionStorage.setItem(this.signerPubkeyStorageKey(), pubkey.toString().trim().toLowerCase());
    } catch (_) {}
  }

  readSignerPubkey() {
    try {
      return sessionStorage.getItem(this.signerPubkeyStorageKey());
    } catch (_) {
      return null;
    }
  }

  storeSignerPhase(phase) {
    try {
      sessionStorage.setItem(this.signerPhaseStorageKey(), phase);
    } catch (_) {}
  }

  readSignerPhase() {
    try {
      return sessionStorage.getItem(this.signerPhaseStorageKey());
    } catch (_) {
      return null;
    }
  }

  clearSignerPhase() {
    try {
      sessionStorage.removeItem(this.signerPhaseStorageKey());
    } catch (_) {}
  }

  validPubkey(value) {
    return /^[0-9a-f]{64}$/i.test(value?.toString().trim() || "");
  }

  normalizeReturnedPubkey(value) {
    const normalized = value?.toString().trim().toLowerCase();
    return this.validPubkey(normalized) ? normalized : null;
  }

  normalizedEventJson(value) {
    const normalized = value?.toString().trim();
    return normalized?.startsWith("{") ? normalized : null;
  }

  normalizeReturnedSignature(value) {
    const normalized = value?.toString().trim();
    return /^[0-9a-f]{128}$/i.test(normalized || "") ? normalized : null;
  }

  persistTurnstileToken() {
    const token = this.turnstileFields().map((field) => field.value).find((value) => value && value.trim().length > 0);
    if (!token) return;

    try {
      sessionStorage.setItem(this.pendingTurnstileStorageKey(), JSON.stringify({
        token,
        capturedAt: Date.now()
      }));
    } catch (_) {}
  }

  restorePendingTurnstileToken() {
    try {
      const raw = sessionStorage.getItem(this.pendingTurnstileStorageKey());
      if (!raw) return;

      const payload = JSON.parse(raw);
      if (!payload?.token) return;
      if (payload.capturedAt && (Date.now() - payload.capturedAt) > 4 * 60 * 1000) {
        sessionStorage.removeItem(this.pendingTurnstileStorageKey());
        return;
      }

      this.turnstileFields().forEach((field) => {
        field.value = payload.token;
      });
    } catch (_) {}
  }

  clearPendingTurnstileToken() {
    try {
      sessionStorage.removeItem(this.pendingTurnstileStorageKey());
    } catch (_) {}
  }

  setStatus(message) {
    if (!this.hasStatusTarget) return;
    this.statusTarget.textContent = message;
    this.statusTarget.hidden = !message;
  }

  signerHostLabel() {
    try {
      if (this.hasCallbackPathValue && this.callbackPathValue) {
        return new URL(this.callbackPathValue, window.location.origin).host;
      }
    } catch (_) {}

    return window.location.host || "nostr.313.cash";
  }

  debugLog(step, detail = "") {
    try {
      fetch(this.debugLogPath, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "same-origin",
        body: JSON.stringify({
          step: `auth_${step}`,
          detail: detail.toString().slice(0, 1400)
        })
      }).catch(() => {});
    } catch (_) {}
  }
}
